import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { Configuration, PublicPaymentService } from '../../api/gen';
import { environment } from '../../environments/environment';
import { CheckoutStart, SettlementQuote, SettlementReceipt } from '../models/settlement.models';

/** Publicly-reachable API host for this page. See `environment.publicApiUrl` for why it is separate. */
const PUBLIC_API_BASE = environment.publicApiUrl.replace(/\/+$/, '');

/**
 * Backend calls for the anonymous settlement page.
 *
 * Every figure on that page comes from `GET /public/payment/quote/{id}` — the one server-side
 * derivation every payment rail shares (KAN-458 D-27). The page never adds, discounts, or
 * re-derives an amount: it displays what the server said and hands the same number back on start,
 * where the server refuses a mismatch rather than quietly charging something else.
 *
 * Unlike {@link PaymentTerminalService} there is no terminal id and no SignalR here. A phone gets
 * its outcome from Stripe's redirect, and the redirect is only a *hint* — the payment is confirmed
 * by re-reading the quote, because a URL parameter is something the parker can type themselves.
 */
@Injectable({ providedIn: 'root' })
export class PublicSettlementService {
  /**
   * Its own client instance, pointed at {@link environment.publicApiUrl} — not the app-wide one the
   * kiosk uses.
   *
   * The DI-provided `PublicPaymentService` is configured with the station's **LAN** address, which
   * is correct for the column and unreachable from a parker's phone. Same endpoints, different host,
   * so the difference lives here rather than in a runtime branch inside every call.
   */
  private readonly api = new PublicPaymentService(
    inject(HttpClient),
    PUBLIC_API_BASE,
    new Configuration({ basePath: PUBLIC_API_BASE }),
  );

  /**
   * Ask the server to re-read this stay's checkout at the provider, right now.
   *
   * Reconciliation otherwise has only two triggers — the provider's webhook, and a fallback sweep
   * that ignores sessions younger than five minutes. Neither fires when the parker lands back here,
   * so a missed webhook left a parker who HAD paid staring at "not confirmed yet" for five minutes,
   * next to a pay button. This is the third trigger and it fires on the return.
   *
   * Called once per confirm and once per manual re-check — never inside the poll loop, which would
   * turn one provider read into fifteen. Failure is deliberately swallowed: the quote poll below is
   * still the only thing that turns this page green, and the webhook and sweeper remain the
   * authoritative backstops.
   */
  async reconcile(parkingProcessId: string): Promise<void> {
    try {
      await firstValueFrom(this.api.publicPaymentReconcileParkingProcessIdPost(parkingProcessId));
    } catch {
      // Nothing to tell the parker: the provider being briefly unreachable says nothing about
      // whether their payment arrived.
    }
  }

  /** What this stay costs to settle right now. Throws on transport / not-found so the page can react. */
  async quote(parkingProcessId: string): Promise<SettlementQuote> {
    const d = await firstValueFrom(this.api.publicPaymentQuoteParkingProcessIdGet(parkingProcessId));
    return {
      parkingProcessId: d.parkingProcessId ?? parkingProcessId,
      amountDue: d.amountDue ?? 0,
      currency: d.currency ?? 'EUR',
      grossAmount: d.grossAmount ?? 0,
      netPaidAmount: d.netPaidAmount ?? 0,
      startedAt: d.startedAt ?? null,
      endedAt: d.endedAt ?? null,
      obligationId: d.obligationId ?? null,
      source: d.source ?? null,
      zoneName: d.parkingZoneName || null,
      spaceName: d.parkingSpaceName || null,
    };
  }

  /**
   * Open a hosted checkout for the quoted amount.
   *
   * `expectedAmount` is the figure the parker actually read on screen. The server compares it with
   * its own quote and refuses the charge if they differ ("Amount Changed") instead of repricing —
   * charging someone a number they were never shown is its own kind of wrong, even when the new
   * number is correct.
   */
  async startCheckout(quote: SettlementQuote, successUrl: string, cancelUrl: string): Promise<CheckoutStart> {
    const d = await firstValueFrom(
      this.api.publicPaymentStartPost({
        parkingProcessId: quote.parkingProcessId,
        expectedAmount: quote.amountDue,
        expectedCurrency: quote.currency,
        successUrl,
        cancelUrl,
      }),
    );

    return {
      paymentSessionId: d.id ?? '',
      checkoutUrl: d.hostedCheckoutDetails?.checkoutUrl ?? '',
    };
  }

  /**
   * The most recent payment session for a stay. Used after the Stripe return to find the session id
   * the fiscal receipt hangs off — the redirect itself carries no id we would trust.
   *
   * Returns the interaction model with it so the caller can tell a hosted checkout from a terminal
   * payment, which decides whether a fiscal receipt exists at all.
   */
  async latestSession(parkingProcessId: string): Promise<{ id: string; isHostedCheckout: boolean } | null> {
    const d = await firstValueFrom(this.api.publicPaymentLookupParkingProcessIdGet(parkingProcessId));
    if (!d.id) return null;
    return { id: d.id, isHostedCheckout: d.interactionModel === 'HostedCheckout' };
  }

  /**
   * Receipt for a paid session: the EFSTA document for a terminal payment, or the provider's own
   * receipt for an online one. `Pending` while the EFSTA worker is still signing.
   */
  async receipt(paymentSessionId: string): Promise<SettlementReceipt> {
    const d = await firstValueFrom(this.api.publicPaymentPaymentSessionIdReceiptGet(paymentSessionId));
    return {
      status: d.status ?? 'NotRequested',
      url: d.receiptUrl ?? null,
      testMode: d.testMode ?? false,
      // Defaults true so an older client that predates the flag keeps its previous meaning.
      isFiscal: (d as { isFiscal?: boolean }).isFiscal ?? true,
    };
  }

  /** The parker opened the receipt: the anonymous endpoints stop handing it out from now on. */
  async markReceiptDownloaded(paymentSessionId: string, parkingProcessId: string): Promise<void> {
    await firstValueFrom(
      this.api.publicPaymentPaymentSessionIdReceiptDownloadedPost(paymentSessionId, { parkingProcessId }),
    );
  }
}
